import { getTableColumnKeys } from "#/config/tableColumnsConfig";
import type { Client } from "#/types/client";

/**
 * Check if a column should be visible for the current client
 *
 * @param tableName - The name of the table (e.g., 'sites', 'devices')
 * @param columnKey - The key of the column to check
 * @param client - The client object with visibleColumns configuration
 * @returns true if the column should be visible, false otherwise
 *
 * Rules:
 * - If client has no visibleColumns config OR visibleColumns is empty for this table: show ALL columns (backward compatible)
 * - If client has visibleColumns config for this table: only show columns in the config
 * - Actions and Checkbox columns are ALWAYS visible (not configurable)
 */
export const isColumnVisible = (
  tableName: string,
  columnKey: string,
  client: Client | null | undefined
): boolean => {
  // Actions column is always visible
  if (columnKey === "actions") {
    return true;
  }

  // If no client or no visibleColumns config, show all columns (default)
  if (!client || !client.visibleColumns) {
    return true;
  }

  // Get the visibleColumns config for this specific table
  const tableVisibleColumns = client.visibleColumns[tableName];

  // If no config for this table, show all columns (default)
  if (!tableVisibleColumns || tableVisibleColumns.length === 0) {
    return true;
  }

  // Check if this column is in the visible columns list
  return tableVisibleColumns.includes(columnKey);
};

/**
 * Get the list of visible columns for a table
 *
 * @param tableName - The name of the table
 * @param client - The client object with visibleColumns configuration
 * @returns Array of visible column keys
 */
export const getVisibleColumns = (
  tableName: string,
  client: Client | null | undefined
): string[] => {
  // If no client or no config, return all column keys
  if (!client || !client.visibleColumns) {
    return getTableColumnKeys(tableName);
  }

  const tableVisibleColumns = client.visibleColumns[tableName];

  // If no config for this table, return all column keys
  if (!tableVisibleColumns || tableVisibleColumns.length === 0) {
    return getTableColumnKeys(tableName);
  }

  return tableVisibleColumns;
};

/**
 * Check if at least one column should be visible
 * Used to determine if we should show the table at all
 */
export const hasVisibleColumns = (
  tableName: string,
  client: Client | null | undefined
): boolean => {
  const visibleColumns = getVisibleColumns(tableName, client);
  return visibleColumns.length > 0;
};
